import { createTheme, alpha, type Theme } from '@mui/material/styles';
import { PRIMARY_CTA_GRADIENT, PRIMARY_CTA_GRADIENT_HOVER } from './gradients';
import {
  getTableRowHoverOverlay,
  getTableRowSelectedHoverOverlay,
  getTableRowSelectedOverlay,
} from './tableRowOverlays';
import { colors } from './colors';
import { focusTokens } from './focus';
import { typography } from './typography';
import { spacing } from './spacing';
import { modalConfig } from './modals';
import { drawerConfig } from './drawers';
import { getCssBaselineScrollbarOverrides } from './scrollbar';

export { focusTokens } from './focus';

// Crear el theme principal de MUI
export const theme = createTheme({
  gradients: {
    primaryCta: PRIMARY_CTA_GRADIENT,
    primaryCtaHover: PRIMARY_CTA_GRADIENT_HOVER,
  },
  palette: {
    primary: colors.primary,
    secondary: colors.secondary,
    success: colors.success,
    warning: colors.warning,
    error: colors.error,
    info: colors.info,
    grey: colors.grey,
    background: colors.background,
    text: colors.text,
  },

  typography: {
    fontFamily: typography.fontFamily,
    h1: typography.h1,
    h2: typography.h2,
    h3: typography.h3,
    h4: typography.h4,
    h5: typography.h5,
    h6: typography.h6,
    body1: typography.body1,
    body2: typography.body2,
    button: typography.button,
    caption: typography.caption,
    overline: typography.overline,
  },

  spacing: spacing.base,

  // Configuración de componentes
  components: {
    MuiCssBaseline: {
      styleOverrides: (theme: Theme) => getCssBaselineScrollbarOverrides(theme),
    },

    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: typography.button.fontFamily,
          fontWeight: typography.button.fontWeight,
          fontSize: typography.button.fontSize,
          textTransform: typography.button.textTransform,
          borderRadius: '8px',
          padding: '8px 16px',
          minHeight: '40px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          },
        },
      },
      variants: [
        {
          // "Tonal button" (Material Design 3): Cancelar / Atrás / Guardar borrador y similares.
          // Sin borde, fondo casi blanco en reposo, un poco más marcado en hover — una sola
          // fuente para todo el sistema (antes cada pantalla lo copiaba a mano por su cuenta).
          // Nombre "tonal" (no "secondary") a propósito: ya existe otra variante "secondary"
          // en components/ui/Button/Button.tsx con un significado distinto (botón relleno verde).
          props: { variant: 'tonal' },
          style: ({ theme }: { theme: Theme }) => ({
            color: theme.palette.text.secondary,
            backgroundColor:
              theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[50],
            border: 'none',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.common.white, 0.12)
                  : theme.palette.grey[100],
              color: theme.palette.text.primary,
              boxShadow: 'none',
            },
          }),
        },
      ],
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            fontFamily: typography.body2.fontFamily,
            fontSize: typography.body2.fontSize,
          },
        },
      },
    },

    /** Bordes unificados (verde teal): idle suave → hover main → focus más oscuro */
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&:hover:not(.Mui-error):not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
            borderColor: focusTokens.hoverBorderColor,
          },
          '&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
            borderColor: focusTokens.borderColor,
            borderWidth: focusTokens.borderWidth,
          },
        },
        notchedOutline: {
          borderWidth: focusTokens.idleBorderWidth,
          borderColor: focusTokens.idleBorderColor,
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused:not(.Mui-error)': {
            color: focusTokens.labelColor,
          },
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        },
      },
    },

    MuiModal: {
      styleOverrides: {
        root: {
          '& .MuiBackdrop-root': {
            backgroundColor: modalConfig.backdrop.backgroundColor,
          },
        },
      },
    },

    // Configuración global para Dialog (modales)
    MuiDialog: {
      styleOverrides: {
        container: {
          alignItems: 'center',
          justifyContent: 'center',
          display: 'flex',
        },
        paper: {
          borderRadius: modalConfig.borderRadius.medium,
          boxShadow: modalConfig.shadows.medium,
          margin: 16,
          marginTop: 'auto',
          marginBottom: 'auto',
          flexShrink: 0,
          boxSizing: 'border-box',
        },
      },
      defaultProps: {
        maxWidth: 'md',
        fullWidth: true,
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: `${modalConfig.headers.default.padding.vertical} ${modalConfig.headers.default.padding.horizontal}`,
          fontFamily: typography.h6.fontFamily,
          fontSize: modalConfig.headers.default.typography.fontSize,
          fontWeight: modalConfig.headers.default.typography.fontWeight,
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: modalConfig.content.default.padding,
        },
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: modalConfig.actions.default.padding,
          backgroundColor: theme.palette.background.paper,
          borderTop: `1px solid ${theme.palette.divider}`,
          gap: modalConfig.actions.default.gap,
        }),
      },
    },

    // Configuración global para Drawer
    MuiDrawer: {
      styleOverrides: {
        paper: {
          boxShadow: drawerConfig.shadows.medium,
        },
      },
    },

    // Configuración global para Alert (notificaciones)
    // Los colores se pueden cambiar desde src/styles/theme/colors.ts
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          fontSize: '0.875rem',
          fontWeight: 500,
        },
        // Personalización de colores por severity
        // 🎨 Degradado exacto del botón "Añadir" del catálogo: linear-gradient(to right, #06b6d4, #10b981)
        standardSuccess: {
          background: PRIMARY_CTA_GRADIENT,
          color: colors.success.contrastText,
          '& .MuiAlert-icon': {
            color: colors.success.contrastText,
          },
        },
        filledSuccess: {
          background: PRIMARY_CTA_GRADIENT,
          color: colors.success.contrastText,
          '& .MuiAlert-icon': {
            color: colors.success.contrastText,
          },
        },
        standardError: {
          backgroundColor: colors.error.main,
          color: colors.error.contrastText,
          '& .MuiAlert-icon': {
            color: colors.error.contrastText,
          },
        },
        filledError: {
          backgroundColor: colors.error.main,
          color: colors.error.contrastText,
          '& .MuiAlert-icon': {
            color: colors.error.contrastText,
          },
        },
        standardWarning: {
          backgroundColor: colors.warning.main,
          color: colors.warning.contrastText,
          '& .MuiAlert-icon': {
            color: colors.warning.contrastText,
          },
        },
        filledWarning: {
          backgroundColor: colors.warning.main,
          color: colors.warning.contrastText,
          '& .MuiAlert-icon': {
            color: colors.warning.contrastText,
          },
        },
        standardInfo: {
          backgroundColor: colors.info.main,
          color: colors.info.contrastText,
          '& .MuiAlert-icon': {
            color: colors.info.contrastText,
          },
        },
        filledInfo: {
          backgroundColor: colors.info.main,
          color: colors.info.contrastText,
          '& .MuiAlert-icon': {
            color: colors.info.contrastText,
          },
        },
      },
    },

    // Configuración global para Snackbar (contenedor de notificaciones)
    MuiSnackbar: {
      styleOverrides: {
        root: {
          '& .MuiSnackbarContent-root': {
            borderRadius: '8px',
          },
        },
      },
    },

    // Hover unificado: tablas, menús, autocomplete y listas en diálogos/popovers
    MuiTableRow: {
      styleOverrides: {
        root: ({ theme }) => {
          const hoverBg = getTableRowHoverOverlay(theme);
          const selectedBg = getTableRowSelectedOverlay(theme);
          const selectedHoverBg = getTableRowSelectedHoverOverlay(theme);

          return {
            transition: 'background 0.2s ease-in-out',
            '.MuiTableBody-root &:hover, &.MuiTableRow-hover:hover': {
              backgroundColor: hoverBg,
            },
            '&.Mui-selected': {
              backgroundColor: selectedBg,
            },
            '&.Mui-selected:hover': {
              backgroundColor: selectedHoverBg,
            },
          };
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&:hover': {
            backgroundColor: getTableRowHoverOverlay(theme),
          },
          '&.Mui-selected': {
            backgroundColor: getTableRowSelectedOverlay(theme),
          },
          '&.Mui-selected:hover': {
            backgroundColor: getTableRowSelectedHoverOverlay(theme),
          },
        }),
      },
    },

    // Autocomplete: un solo celeste al resaltar (aria-selected = highlight, no selección de tabla)
    MuiAutocomplete: {
      styleOverrides: {
        listbox: ({ theme }) => {
          const highlightBg = getTableRowHoverOverlay(theme);
          return {
            '& .MuiAutocomplete-option': {
              '&.Mui-focused, &:hover, &[aria-selected="true"], &[aria-selected="true"].Mui-focused':
                {
                  backgroundColor: highlightBg,
                },
            },
          };
        },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          '.MuiDialog-root &, .MuiPopover-root &, .MuiMenu-root &': {
            '&:hover': {
              backgroundColor: getTableRowHoverOverlay(theme),
            },
            '&.Mui-selected': {
              backgroundColor: getTableRowSelectedOverlay(theme),
            },
            '&.Mui-selected:hover': {
              backgroundColor: getTableRowSelectedHoverOverlay(theme),
            },
          },
        }),
      },
    },

    // Configuración global para Switch (toggles/flags)
    // Diseño inspirado en switches modernos: círculo con mismo radio que la barra
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 40,
          height: 20,
          padding: 0,
        },
        switchBase: {
          padding: 2,
          '&.Mui-checked': {
            transform: 'translateX(20px)',
            color: '#ffffff',
            '& + .MuiSwitch-track': {
              backgroundColor: '#34d399', // Verde brillante vibrante como en la segunda foto
              opacity: 1,
              border: 'none',
            },
          },
          '&.Mui-disabled': {
            '& + .MuiSwitch-track': {
              opacity: 0.3,
            },
          },
        },
        thumb: {
          width: 16, // Exactamente el mismo tamaño que la altura del track (20px - 4px padding = 16px)
          height: 16,
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
        },
        track: {
          borderRadius: 10, // Radio igual a la mitad de la altura (20px / 2 = 10px) - completamente redondeado
          backgroundColor: '#d1d5db',
          opacity: 1,
          transition:
            'background-color 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, border-color 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
        },
        // Estilos específicos para size="small" (usado en SalesStep2)
        sizeSmall: {
          width: 34,
          height: 18,
          '& .MuiSwitch-switchBase': {
            padding: 1.5,
            '&.Mui-checked': {
              transform: 'translateX(16px)', // 34px - 18px (thumb) - 0px = 16px
            },
          },
          '& .MuiSwitch-thumb': {
            width: 15, // Exactamente el mismo tamaño que la altura del track (18px - 3px padding = 15px)
            height: 15,
          },
          '& .MuiSwitch-track': {
            borderRadius: 9, // Radio igual a la mitad de la altura (18px / 2 = 9px) - completamente redondeado
          },
        },
      },
    },
  },
});

// Exportar también los objetos individuales para uso directo
export { colors, typography, spacing, modalConfig, drawerConfig };
export { subtleScrollbarSx, getCssBaselineScrollbarOverrides, SCROLLBAR_SIZE } from './scrollbar';
export type { ScrollbarVariant } from './scrollbar';
export { PRIMARY_CTA_GRADIENT, PRIMARY_CTA_GRADIENT_HOVER } from './gradients';
export {
  getTableRowHoverOverlay,
  getSelectableRowHoverOverlay,
  getTableRowSelectedOverlay,
  getTableRowSelectedHoverOverlay,
  selectableListItemHoverSx,
  tableRowTransparentSelectionSx,
} from './tableRowOverlays';
